import React, { useState, useContext, createContext, useEffect } from "react";
import { Bell, ChevronDown, User, LogOut } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { useAIAssistant } from "../context/AIAssistantContext";
import AIAssistantPanel from "./organization/AIAssistantPanel";

// Create a context for dynamic title updates
export const TitleContext = createContext<{
  dynamicTitle: string | null;
  setDynamicTitle: (title: string | null) => void;
}>({
  dynamicTitle: null,
  setDynamicTitle: () => {},
});

interface TopbarProps {
  title: string;
}

const Topbar: React.FC<TopbarProps> = ({ title }) => {
  const { user, accessToken, setUserAndToken } = useUser();
  const { dynamicTitle } = useContext(TitleContext);
  const { 
    isAIAssistantOpen, 
    setIsAIAssistantOpen, 
    currentOrganizationId, 
    currentOrganizationName,
    setCurrentOrganization 
  } = useAIAssistant();
  const [isAIPanelVisible, setIsAIPanelVisible] = useState(false);
  const location = useLocation();
  const { organizationId } = useParams<{ organizationId: string }>();

  // Use dynamic title if available, otherwise use the prop title
  const displayTitle = dynamicTitle || title;

  // Check if we're on an organization details page
  const isOrganizationDetailsPage = location.pathname.includes('/organizations/') && organizationId;

  // Update organization context when on organization details page
  useEffect(() => {
    if (isOrganizationDetailsPage && organizationId) {
      // Try to get organization name from localStorage cache
      const cachedOrgName = localStorage.getItem(`org_name_${organizationId}`);
      if (cachedOrgName) {
        setCurrentOrganization(organizationId, cachedOrgName);
      } else {
        // If not cached, we'll need to fetch it from the organization data
        // For now, just set the ID
        setCurrentOrganization(organizationId, null);
      }
    }
  }, [isOrganizationDetailsPage, organizationId, setCurrentOrganization]);

  const handleLogout = async () => {
    try {
      await fetch(
        (process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000") + "/api/v1/admin/logout",
        {
          method: "POST",
          credentials: "include",
        }
      );
    } catch {}
    setUserAndToken(null, null);
    window.location.href = "/login";
  };

  const getInitials = () => {
    const name = user?.name || "Admin User";
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return nameParts[0].slice(0, 2).toUpperCase();
  };

  const getFullName = () => {
    return user?.name || "Admin User";
  };

  const openAIModal = () => {
    setIsAIAssistantOpen(true);
    // Start with panel hidden, then show it after a brief delay to enable transition
    setIsAIPanelVisible(false);
    setTimeout(() => {
      setIsAIPanelVisible(true);
    }, 10); // Small delay to ensure the element is mounted before transition
  };
  
  const closeAIModal = () => {
    setIsAIPanelVisible(false);
    // Delay the actual state change to allow transition to complete
    setTimeout(() => {
      setIsAIAssistantOpen(false);
    }, 10); // Match the transition duration
  };

  return (
    <>
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-8">
        <div className="flex items-center">
          <h1 className="text-xl font-semibold text-slate-900">{displayTitle}</h1>
        </div>
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button
            className="relative text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg p-2 transition"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          {/* Profile Dropdown */}
          <div className="relative group">
            <button className="flex items-center space-x-3 hover:bg-slate-100 rounded-lg px-2 py-1 transition">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-xs font-medium text-blue-700">{getInitials()}</span>
              </div>
              <span className="text-sm font-medium text-slate-700">{getFullName()}</span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            {/* Dropdown menu (simple version) */}
            <div className="hidden group-hover:block absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
              <Link to="/profile" className="flex items-center px-4 py-2 text-slate-700 hover:bg-slate-100">
                <User className="w-4 h-4 mr-2" /> Profile
              </Link>
              <div className="border-t border-slate-100 my-1" />
              <button onClick={handleLogout} className="flex items-center px-4 py-2 text-red-600 hover:bg-slate-100 w-full">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* AI Assistant Floating Button - Only show on organization details pages and when panel is closed */}
      {isOrganizationDetailsPage && !isAIAssistantOpen && (
        <button
          onClick={openAIModal}
          className="fixed bottom-6 right-6 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out z-50 flex items-center justify-center animate-in fade-in slide-in-from-bottom-2 border-2 border-blue-100"
          aria-label="Open ARiA Assistant"
        >
          <img 
            src="/ARiA-Logo-PrimaryLightBlue-Transparent-BG.png" 
            alt="ARiA Logo" 
            className="w-8 h-8"
          />
        </button>
      )}
      
      {/* AI Assistant Panel - Show when open */}
      {isAIAssistantOpen && (
        <AIAssistantPanel
          organizationId={currentOrganizationId || 'default'}
          organizationName={currentOrganizationName}
          accessToken={accessToken}
          setUserAndToken={setUserAndToken}
          onClose={closeAIModal}
          isVisible={isAIPanelVisible}
        />
      )}
    </>
  );
};

export default Topbar; 